import { csrfToken } from '@/utils/index';

function handleError(response, req, error) {
  // ignore errors caused by lost internet connection
  if (response.status === 0 || response.status === undefined || req.url === window.__DATA_TRACKING_ENDPOINT || response.status === 422) {
    return;
  }

  const errorLevel = response.status >= 500 ? 'error' : 'warning';

  Rollbar[errorLevel](`Ajax ${errorLevel}: ${response.status} on ${req.method}:${req.url}`, {
    url: req.url,
    status: response.status,
    statusText: response.statusText,
    responseText: response.responseText,
    type: req.method,
    data: req.data,
    error,
  });
}

/**
 * Makes an HTTP request using the Fetch API with additional handling for CSRF tokens and error handling.
 *
 * @param {string} url - The URL to which the request is made.
 * @param {Object} [options={}] - The options object to configure the request.
 * @param {string} [options.method='GET'] - The HTTP method (e.g., GET, POST, PUT, DELETE).
 * @param {Object} [options.headers={}] - An object representing custom headers to include in the request.
 * @param {Object} [options.data] - Data to be sent as the body of the request. This will be JSON-stringified and
 *                                   include a CSRF token by default.
 * @param {FormData} [options.formData] - A FormData object to send as the body of the request.
 *                                  This will include a CSRF token by default.
 * @returns {Promise<Object>} A promise that resolves with the parsed JSON response data or rejects with an error.
 *
 * @example
 * // Example of a GET request
 * request('/api/example')
 *   .then(data => console.log(data))
 *   .catch(error => console.error(error));
 *
 * @example
 * // Example of a POST request with data
 * request('/api/example', {
 *   method: 'POST',
 *   data: { name: 'John Doe', age: 30 },
 * })
 *   .then(data => console.log(data))
 *   .catch(error => console.error(error));
 */
export default function request(url, options = {}) {
  return new Promise((resolve, reject) => {
    options.headers = options.headers || {};

    if (options.formData) {
      options.formData.append('_token', csrfToken());

      options.body = options.formData;
    } else if (options.data) {
      options.body = JSON.stringify({
        ...options.data,
        _token: csrfToken(),
      });

      options.headers['Content-Type'] = 'application/json';
    } else if (options.method === 'POST') {
      options.body = JSON.stringify({
        _token: csrfToken(),
      });

      options.headers['Content-Type'] = 'application/json';
    } else if (options.method === 'DELETE') {
      options.headers['X-CSRF-TOKEN'] = csrfToken();
    }

    // just for handleError
    options.url = url;

    // to let know backend the request type
    options.headers['X-Requested-With'] = 'XMLHttpRequest';

    fetch(url, options)
      .then(response => {
        // HTTP errors (e.g., 404 or 500): fetch resolves successfully
        if (!response.ok) {
          handleError(response, options, response.statusText);
          reject(response);

          return response;
        }

        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('application/json')) {
          return response.json(); // Parse JSON
        } if (contentType && contentType.includes('text/')) {
          return response.text(); // Parse plain text or HTML
        } if (contentType && contentType.includes('application/octet-stream')) {
          return response.blob(); // Handle binary data (e.g., images, files)
        }
        return response; // Return the response as-is
      })
      .then(data => resolve(data))
      .catch((error) => {
        if (error instanceof Response) {
          // This is an HTTP error already handled above
          return;
        }
        // The fetch API only rejects the returned promise if a network error occurs
        // (e.g., no internet connection, DNS failure, CORS issue).
        handleError({}, options, error);
        reject(error);
      });
  });
}
